import { useCallback, useRef } from 'react';

/**
 * Hooks to translate stepper by x px, to make sure current step appears on the stepper
 */
export const useSteppersImpl = ({
  activeIndex,
  containerRef,
}: {
  /**
   * Current active index of the step
   */
  activeIndex: number;
  /**
   * Ref for the container of the steppers, used to get the width of the container
   */
  containerRef?: React.MutableRefObject<any>;
}) => {
  const stepsIndexRefs = useRef({});

  /**
   * Total width of all the steps component
   */
  const totalStepperWidth = Object.entries(stepsIndexRefs.current)
    .map(val => {
      return (val[1] as unknown as any)?.clientWidth ?? 0;
    })
    .reduce((prev, curr) => parseFloat(prev) + parseFloat(curr) + 12, [0]);

  /**
   * a function to translate the stepper by x amount of px, by using the width information of each step component,
   * for example, if there are n steps with the width [a, b, c, ...] and the current step is index 2,
   * then the stepper will be translated by min(a + b + c, totalStepperWidth).
   */
  const translateStepper = useCallback(() => {
    return containerRef &&
      totalStepperWidth > containerRef?.current?.clientWidth
      ? Math.min(
          Math.abs(
            containerRef?.current?.clientWidth ?? 0 - totalStepperWidth,
          ) ?? 0,
          Object.entries(stepsIndexRefs.current)
            .splice(0, activeIndex)
            .map(val => {
              return (val[1] as unknown as any)?.clientWidth ?? 0;
            })
            .reduce(
              (prev, curr) => parseFloat(prev) + parseFloat(curr) + 12,
              [0],
            ),
        )
      : window.innerWidth < 768
      ? /**
         * Case if the container ref is not defined
         */
        Object.entries(stepsIndexRefs.current)
          .splice(0, activeIndex)
          .map(val => {
            return (val[1] as unknown as any)?.clientWidth ?? 0;
          })
          .reduce((prev, curr) => parseFloat(prev) + parseFloat(curr) + 12, [0])
      : 0;
  }, [activeIndex, containerRef, totalStepperWidth]);

  return { translateStepper, stepsIndexRefs, containerRef };
};
